import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { ListTable } from '@visactor/vtable';
import * as VTable from '@visactor/vtable';
import { useTableData } from '../../hooks/useTableData';
import GridToolbar from './GridToolbar';
import { MultiSelectEditor, MultiSelectEditorOptions } from './MultiSelectEditor';
import { TableColumn } from '../../types';
import { TABLE_CONFIG } from '../../utils/constants';
import './styles.css';
import { InputEditor } from '@visactor/vtable-editors';
import * as VTablePlugins from '@visactor/vtable-plugins';
import * as VUtils from '@visactor/vutils'
import {
  downloadExcel,
  exportVTableToExcel,
  downloadCsv,
  exportVTableToCsv
} from '@visactor/vtable-export';
import tableService from '../../services/tableService';
import AIInputModal from './AIInputModal';

interface DataTableProps {
  tableId: string | null;
  /** Directus collection 名称，按 table_id + is_active=true 筛选 */
  collection?: string | null;
  data: any[];
  changedCells?: any[];
  refreshTrigger?: number;
  onDataUpdate: (data: any[]) => void;
  exportExcelTrigger?: number;
  exportCsvTrigger?: number;
  onAIQuestion?: (question: string, cellContext?: any) => void;
}

interface CellContext {
  row: number;
  col: number;
  cellValue: any;
  columnName: string;
  fieldName: string;
  rowId: string;
}

// ─── 列指纹：只比较字段名和类型，忽略引用 ─────────────────────

const computeColumnsFingerprint = (cols: readonly TableColumn[]): string => {
  if (!cols.length) return '';
  return cols.map(c => `${c.field}|${c.cellType ?? ''}|${c.title}`).join(';;');
};

// ─── 常量提取到组件外，避免每次 render 重建 ─────────────────────

const TAG_COLORS = [
  { bg: '#e3f2fd', text: '#1976d2' },
  { bg: '#f3e5f5', text: '#7b1fa2' },
  { bg: '#e8f5e8', text: '#388e3c' },
  { bg: '#fff3e0', text: '#f57c00' },
  { bg: '#ffebee', text: '#d32f2f' },
  { bg: '#e0f2f1', text: '#00796b' },
  { bg: '#fce4ec', text: '#c2185b' },
  { bg: '#f1f8e9', text: '#689f38' },
  { bg: '#e8eaf6', text: '#3f51b5' },
  { bg: '#fff8e1', text: '#fbc02d' },
  { bg: '#efebe9', text: '#5d4037' },
  { bg: '#eceff1', text: '#455a64' },
  { bg: '#f3e5f5', text: '#8e24aa' },
  { bg: '#e1f5fe', text: '#0277bd' },
  { bg: '#f9fbe7', text: '#827717' },
  { bg: '#fff3e0', text: '#ef6c00' },
  { bg: '#fce4ec', text: '#ad1457' },
  { bg: '#e0f7fa', text: '#00695c' },
  { bg: '#f1f8e9', text: '#558b2f' },
  { bg: '#fff9c4', text: '#f9a825' },
  { bg: '#ffebee', text: '#c62828' },
  { bg: '#e8eaf6', text: '#283593' },
  { bg: '#efebe9', text: '#4e342e' },
  { bg: '#fafafa', text: '#424242' },
] as const;

const TAG_HEIGHT = 24;
const TAG_GAP = 8;
const NUMBER_WIDTH = 40;
const CELL_PADDING = 16;

const DataTable: React.FC<DataTableProps> = ({
  tableId,
  collection,
  data: externalData,
  changedCells,
  refreshTrigger,
  onDataUpdate,
  exportExcelTrigger,
  exportCsvTrigger,
  onAIQuestion,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<ListTable | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // ★ 用 ref 存数据，避免 useCallback/useEffect 依赖数据引用
  const currentDataRef = useRef<any[]>([]);
  const columnsRef = useRef<TableColumn[]>([]);
  const vtableColumnsRef = useRef<any[]>([]);
  const onDataUpdateRef = useRef(onDataUpdate);
  onDataUpdateRef.current = onDataUpdate;

  // ★ 列指纹 ref，用来判断列定义是否真正变化
  const columnsFingerprintRef = useRef<string>('');

  // ★ 重建计数器：只有列真正变化时才递增
  const [rebuildVersion, setRebuildVersion] = useState(0);

  // AI 输入框状态
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [currentCellContext, setCurrentCellContext] = useState<CellContext | undefined>();

  const {
    data: internalData,
    columns,
    loading,
    error,
    updateData,
    refreshData,
    validateData,
  } = useTableData(tableId || undefined, refreshTrigger, collection || undefined);

  // ★ 用 tableId 决定数据源
  const currentData = tableId ? internalData : [];
  currentDataRef.current = currentData;
  columnsRef.current = columns;

  // ─── VTable 基础引用 ─────────────────────

  const VGroup = VTable.VGroup;
  const VText = VTable.VText;
  const cloneDeep = VUtils.cloneDeep;

  // ─── ★ 检测列是否真正变化，只在变化时触发重建 ─────────────────────

  useEffect(() => {
    const newFingerprint = computeColumnsFingerprint(columns);
    if (newFingerprint && newFingerprint !== columnsFingerprintRef.current) {
      columnsFingerprintRef.current = newFingerprint;
      // ★ 同步构建 vtableColumns 并存入 ref
      vtableColumnsRef.current = buildVTableColumns(columns);
      setRebuildVersion(v => v + 1);
    }
  }, [columns]);

  // ─── 标签相关工具函数（纯函数，不依赖 state） ─────────────────────

  const getTagColorIndex = useCallback((label: string): number => {
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      const char = label.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash | 0;
    }
    return Math.abs(hash) % TAG_COLORS.length;
  }, []);

  const formatTagItem = useCallback((item: any, options: any, tableInstance: any) => {
    const {
      blankWidth = 16,
      fontWeight = 400,
      fontSize = 14,
      fontFamily = 'PingFang SC',
      basePadding = CELL_PADDING * 2,
      cellWidth,
      tagIndex = 0,
    } = options;
    const tagMinWidth = 48;
    const tagMaxWidth = Math.max(cellWidth - basePadding, tagMinWidth);

    const label = (item.label || '').replace(/[\u200B\u2060]/g, '');
    const { width } = tableInstance.measureText(label, { fontSize, fontWeight, fontFamily });
    const textLength = Math.ceil(width) + blankWidth;
    const tagWidth = Math.min(Math.max(textLength, tagMinWidth), tagMaxWidth);

    const colorIndex = getTagColorIndex(label);
    const tagColor = TAG_COLORS[colorIndex];

    if (!item.tagProps) {
      item.tagProps = { fontColor: tagColor.text, backgroundColor: tagColor.bg };
    } else {
      item.tagProps.fontColor = tagColor.text;
      item.tagProps.backgroundColor = tagColor.bg;
    }
    item.tagProps.width = tagWidth;

    return { textLength, tagWidth, tagMaxWidth, tagMinWidth, item };
  }, [getTagColorIndex]);

  const calcRowCount = useCallback((cellHeight: number, options?: any) => {
    const { lineHeight = 24, paddingTop = 4, paddingBottom = 4, lineGap = 8 } = options || {};
    const availableHeight = cellHeight - paddingTop - paddingBottom;
    const rows = Math.floor((availableHeight - lineHeight) / (lineHeight + lineGap)) + 1;
    return Math.max(1, rows);
  }, []);

  const calcCellTags = useCallback((tags: any[] = [], options: any, tableInstance: any) => {
    const { rowCount = 1 } = options;
    if (!tags.length) return { filterTags: [], filterTagsRowMap: {} };
    const data = cloneDeep(tags);

    const filterTags: any[] = [];
    const filterTagsRowMap: { [key: number]: any[] } = {};
    const numberTagWidth = TAG_GAP + NUMBER_WIDTH;
    const len = data.length;
    let totalWidth = 0;
    let currentRow = 1;

    const addTags = (item: any) => {
      filterTags.push(item);
      if (!filterTagsRowMap[currentRow]) filterTagsRowMap[currentRow] = [];
      filterTagsRowMap[currentRow].push(item);
    };

    if (len === 1) {
      const { item } = formatTagItem(data[0], { ...options, tagIndex: 0 }, tableInstance);
      addTags(item);
    } else if (len > 1) {
      for (let i = 0; i < len; i += 1) {
        const { item, tagMaxWidth, tagMinWidth, textLength, tagWidth } = formatTagItem(data[i], { ...options, tagIndex: i }, tableInstance);

        const handlePushItem = (pushItem: any) => {
          const calcWidth = totalWidth + tagMinWidth + (i === len - 1 ? 0 : numberTagWidth);
          if (tagMaxWidth >= calcWidth) {
            const realTextWidth = Math.min(tagWidth, tagMaxWidth - totalWidth - (i === len - 1 ? 0 : numberTagWidth));
            pushItem.tagProps.width = realTextWidth;
            totalWidth = totalWidth + realTextWidth + TAG_GAP;
            addTags(pushItem);
            return false;
          } else if (tagMaxWidth < calcWidth) {
            if (tagMaxWidth < totalWidth + tagMinWidth + numberTagWidth) {
              addTags({ label: '+' + (len - filterTags.length), value: -1, tagProps: { fontColor: '#404A59', backgroundColor: '#F5F5F5', width: NUMBER_WIDTH } });
              return true;
            }
            if (i === len - 1) {
              pushItem.tagProps.width = Math.min(tagWidth, tagMaxWidth - totalWidth);
              addTags(pushItem);
            } else {
              pushItem.tagProps.width = Math.min(tagWidth, tagMaxWidth - totalWidth - numberTagWidth);
              addTags(pushItem);
              addTags({ label: '+' + (len - filterTags.length), value: -1, tagProps: { fontColor: '#404A59', backgroundColor: '#F5F5F5', width: NUMBER_WIDTH } });
            }
            return true;
          }
          return false;
        };

        if (currentRow >= rowCount) {
          if (handlePushItem(item)) break;
        } else {
          const calcWidth = totalWidth + textLength;
          if (tagMaxWidth >= calcWidth || totalWidth === 0) {
            const realTextWidth = Math.min(tagWidth, tagMaxWidth - totalWidth);
            item.tagProps.width = realTextWidth;
            item.tagProps.boundsPadding = [0, 6, 6, 0];
            totalWidth = totalWidth + realTextWidth + TAG_GAP;
            addTags(item);
          } else {
            currentRow += 1;
            totalWidth = 0;
            if (currentRow < rowCount) {
              totalWidth = totalWidth + tagWidth + TAG_GAP;
              item.tagProps.boundsPadding = [0, 6, 6, 0];
              addTags(item);
            } else {
              if (handlePushItem(item)) break;
            }
          }
        }
      }
    }
    return { filterTags, filterTagsRowMap };
  }, [formatTagItem, cloneDeep]);

  const normalizeTagsData = useCallback((tags: any[]): any[] => {
    if (!Array.isArray(tags) || tags.length === 0) return [];
    return tags.map((item) => {
      if (typeof item === 'object' && item !== null && 'label' in item) return { ...item };
      if (typeof item === 'string') return { label: item, value: item };
      return { label: String(item), value: String(item) };
    });
  }, []);

  const getTagsRowMap = useCallback((tags: any, options: any, tableInstance: any) => {
    const normalizedTags: any = normalizeTagsData(tags);
    const rowCount = calcRowCount(options.height);
    const res = calcCellTags(normalizedTags, { cellWidth: options.width, rowCount, blankWidth: options.blankWidth }, tableInstance);
    return res.filterTagsRowMap;
  }, [normalizeTagsData, calcRowCount, calcCellTags]);

  // ─── 构建 VTable 列配置（提取为普通函数，不用 useMemo） ─────────────────────

  const buildVTableColumns = useCallback((cols: TableColumn[]) => {
    const inputEditor = new InputEditor();
    VTable.register.editor('input-editor', inputEditor);

    const multiSelectEditors = new Map<string, MultiSelectEditor>();

    return cols.map((col: TableColumn) => {
      const cellType = {
        ...(col.cellType !== 'singleSelect' && col.cellType !== 'multiSelect' && col.cellType !== 'input' && { cellType: col.cellType }),
      };
      const baseColumn = {
        field: col.field,
        title: col.title,
        width: col.width || TABLE_CONFIG.DEFAULT_COLUMN_WIDTH,
        minWidth: col.minWidth || TABLE_CONFIG.MIN_COLUMN_WIDTH,
        ...cellType,
        headerStyle: {
          bgColor: '#f8fafc',
          color: '#374151',
          fontWeight: '600',
          fontSize: 14,
          borderColor: '#e5e7eb',
        },
        style: {
          color: col.cellType === 'button' ? '#ffffff' : '#374151',
          fontSize: 14,
          borderColor: '#f3f4f6',
          buttonStyle: { buttonColor: '#667eea', buttonBorderColor: '#667eea', buttonHoverColor: '#5a67d8' },
        },
      };

      if (col.cellType === 'input') {
        return { ...baseColumn, editor: 'input-editor' } as any;
      }

      if (col.cellType === 'multiSelect' || col.cellType === 'singleSelect') {
        const editorKey = `select-editor-${col.field}`;
        if (!multiSelectEditors.has(editorKey)) {
          const editorConfig: MultiSelectEditorOptions = {
            options: col.options || [],
            placeholder: `请选择${col.title}...`,
            maxHeight: 150,
            searchable: true,
            isSingleSelect: col.cellType === 'singleSelect',
          };
          const editor = new MultiSelectEditor(editorConfig);
          multiSelectEditors.set(editorKey, editor);
          VTable.register.editor(editorKey, editor);
        }

        return {
          ...baseColumn,
          editor: editorKey,
          customLayout: (args: any) => {
            const { table, row, column, rect } = args;
            const { width, height } = rect || table.getCellRect(column, row);
            const record = table.getRecordByRowCol(column, row);
            const tagsMap = getTagsRowMap(record[col.field], { width, height, blankWidth: 34 }, table) || {};

            const container = (
              <VGroup attribute={{ width: width - 32, height, display: 'flex', alignItems: 'center', alignContent: 'center', dx: 16 }}>
                {Object.keys(tagsMap).map((key: any) => {
                  const tag = tagsMap[key];
                  return (
                    <VGroup key={key} attribute={{ width: width - 32, height: TAG_HEIGHT, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden', boundsPadding: [4, 0, 4, 0] }}>
                      {tag.map((item: any, subIndex: number) => (
                        <VGroup key={item.id || `tag-${subIndex}`} attribute={{ width: item.tagProps.width, height: TAG_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cornerRadius: TAG_HEIGHT - 1, fill: item.tagProps.backgroundColor, lineWidth: 1, boundsPadding: subIndex > 0 ? [0, 0, 0, 8] : 0 }}>
                          <VText attribute={{ text: item.label, fontSize: 14, fill: item.tagProps.fontColor, maxLineWidth: item.value !== -1 ? item.tagProps.width - 34 : item.tagProps.width }} />
                        </VGroup>
                      ))}
                    </VGroup>
                  );
                })}
              </VGroup>
            );
            return { rootContainer: container, renderDefault: false };
          },
        } as any;
      }

      return baseColumn as any;
    });
  }, [getTagsRowMap]);

  // ─── 表格清理 ─────────────────────

  const _cleanupTable = useCallback(() => {
    if (tableInstanceRef.current) {
      try {
        tableInstanceRef.current.release();
      } catch (err) {
        console.error('清理表格失败:', err);
      }
      tableInstanceRef.current = null;
    }
  }, []);

  // ─── ★ 表格初始化：通过 ref 读取一切，不依赖任何 state ─────────────────────

  const _initializeTable = useCallback(() => {
    if (!containerRef.current || tableInstanceRef.current || !isMountedRef.current) return;
    if (vtableColumnsRef.current.length === 0) return;

    try {
      const tableInstance = new ListTable(containerRef.current, {
        columns: vtableColumnsRef.current,
        records: currentDataRef.current,
        defaultRowHeight: TABLE_CONFIG.DEFAULT_ROW_HEIGHT,
        tooltip: { isShowOverflowTextTooltip: true },
        rowSeriesNumber: { title: '', dragOrder: true, width: 'auto' },
        animationAppear: { duration: 300, delay: 100, type: 'one-by-one', direction: 'row' },
        theme: {
          headerStyle: { bgColor: '#f8fafc', borderColor: '#e5e7eb' },
          bodyStyle: {
            bgColor: '#ffffff',
            borderColor: '#f3f4f6',
            hover: { cellBgColor: '#f9fafb' },
            select: { cellBgColor: '#ffffff' },
          },
          selectionStyle: { cellBorderLineWidth: 1 },
        },
        editCellTrigger: 'click',
        emptyTip: { text: 'no data records' },
      });

      // ─── 事件绑定（全部通过 ref 读数据） ─────

      tableInstance.on('change_cell_value', async (args: any) => {
        if (!isMountedRef.current) return;
        const { col, row, changedValue, rawValue } = args;
        const rowIndex = row - 1;
        const colIndex = col - 1;

        if (changedValue === rawValue) return;
        if (Array.isArray(changedValue) && Array.isArray(rawValue)) {
          if (changedValue.length === rawValue.length && changedValue.every((v: any, i: number) => v === rawValue[i])) return;
        }

        const data = currentDataRef.current;
        const cols = vtableColumnsRef.current;
        const rowData = data[rowIndex];

        if (rowData && cols[colIndex]) {
          const fieldName = cols[colIndex].field;
          rowData[fieldName] = changedValue;
          const rowId = rowData?.id;
          if (rowId && tableId) {
            try {
              await tableService.updateCellData({ tableId, rowId, field: fieldName, value: changedValue, oldValue: rawValue });
            } catch (err) {
              console.error('更新单元格数据失败:', err);
            }
          }
        }
      });

      tableInstance.on('click_cell', (args: any) => {
        if (!isMountedRef.current) return;
        const { col, row } = args;
        const rect = tableInstance.getVisibleCellRangeRelativeRect({ col, row });
        tableInstance.showDropDownMenu(col, row, {
          content: [{
            text: '',
            menuKey: 'ai',
            icon: {
              width: 16, height: 16,
              svg: '<svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M16.6001 6.78677C16.4694 6.21177 15.9581 5.80371 15.3684 5.80371C14.7787 5.80371 14.2675 6.21177 14.1367 6.78677L12.9698 11.9186C12.6466 13.3402 11.5365 14.4503 10.1149 14.7735L4.98312 15.9404C4.40806 16.0712 4 16.5824 4 17.1721C4 17.7619 4.40806 18.2731 4.98312 18.4039L10.1149 19.5708C11.5365 19.894 12.6466 21.0041 12.9698 22.4257L14.1367 27.5575C14.2675 28.1325 14.7787 28.5406 15.3684 28.5406C15.9581 28.5406 16.4694 28.1325 16.6001 27.5575L17.767 22.4257C18.0904 21.0041 19.2004 19.894 20.622 19.5708L25.7538 18.4039C26.3288 18.2731 26.7369 17.7619 26.7369 17.1721C26.7369 16.5824 26.3288 16.0712 25.7538 15.9404L20.622 14.7735C19.2004 14.4503 18.0904 13.3402 17.767 11.9186L16.6001 6.78677Z" fill="#000000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M24.0583 4.92283C24.0074 4.69922 23.8086 4.54053 23.5793 4.54053C23.3499 4.54053 23.1511 4.69922 23.1003 4.92283L22.6465 6.91854C22.5208 7.47137 22.089 7.90309 21.5362 8.02879L19.5405 8.48259C19.3169 8.53343 19.1582 8.73226 19.1582 8.96159C19.1582 9.19092 19.3169 9.38974 19.5405 9.44058L21.5362 9.89438C22.089 10.0201 22.5208 10.4518 22.6465 11.0046L23.1003 13.0003C23.1511 13.224 23.3499 13.3826 23.5793 13.3826C23.8086 13.3826 24.0074 13.224 24.0583 13.0003L24.5121 11.0046C24.6378 10.4518 25.0695 10.0201 25.6223 9.89438L27.618 9.44058C27.8416 9.38974 28.0003 9.19092 28.0003 8.96159C28.0003 8.73226 27.8416 8.53343 27.618 8.48259L25.6223 8.02879C25.0695 7.90309 24.6378 7.47137 24.5121 6.91854L24.0583 4.92283Z" fill="#000000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.78089 22.4709C7.74821 22.3271 7.62039 22.2251 7.47297 22.2251C7.32554 22.2251 7.19773 22.3271 7.16504 22.4709L6.87331 23.7538C6.7925 24.1092 6.51497 24.3867 6.15958 24.4676L4.87664 24.7593C4.73288 24.792 4.63086 24.9198 4.63086 25.0672C4.63086 25.2146 4.73288 25.3424 4.87664 25.3751L6.15958 25.6669C6.51497 25.7477 6.7925 26.0252 6.87331 26.3806L7.16504 27.6636C7.19773 27.8073 7.32554 27.9093 7.47297 27.9093C7.62039 27.9093 7.74821 27.8073 7.78089 27.6636L8.07262 26.3806C8.15345 26.0252 8.43096 25.7477 8.78635 25.6669L10.0693 25.3751C10.2131 25.3424 10.3151 25.2146 10.3151 25.0672C10.3151 24.9198 10.2131 24.792 10.0693 24.7593L8.78635 24.4676C8.43096 24.3867 8.15345 24.1092 8.07262 23.7538L7.78089 22.4709Z" fill="#000000"/></svg>',
            },
          }],
          position: { x: rect.bounds.x2 + 2, y: rect.bounds.y1 },
        });
      });

      tableInstance.on('dropdown_menu_click', (args: any) => {
        if (args.menuKey === 'ai') {
          const { col: menuCol, row: menuRow } = args;
          const record = tableInstance.getRecordByRowCol(menuCol, menuRow);
          const column = vtableColumnsRef.current[menuCol - 1];
          if (column && record) {
            setCurrentCellContext({
              row: menuRow,
              col: menuCol,
              cellValue: record[column.field],
              columnName: column.title,
              fieldName: column.field,
              rowId: record.id,
            });
            setIsAIModalOpen(true);
          }
        }
      });

      tableInstanceRef.current = tableInstance;
    } catch (err) {
      console.error('初始化表格失败:', err);
    }
  }, [tableId]); // ★ 只依赖 tableId

  // ─── AI 相关回调 ─────────────────────

  const _handleAIQuestion = useCallback(async (question: string, cellContext?: CellContext) => {
    if (!onAIQuestion) return;
    try {
      await onAIQuestion(question, cellContext);
    } catch (err) {
      console.error('发送AI问题失败:', err);
      throw err;
    }
  }, [onAIQuestion]);

  const _handleCloseAIModal = useCallback(() => {
    setIsAIModalOpen(false);
    setCurrentCellContext(undefined);
  }, []);

  const _handleResize = useCallback(() => {
    if (!isMountedRef.current || !tableInstanceRef.current || !containerRef.current) return;
    try {
      tableInstanceRef.current.resize();
    } catch (err) {
      console.error('表格大小调整失败:', err);
    }
  }, []);

  const _exportToExcel = useCallback(async () => {
    if (!tableInstanceRef.current) return;
    try {
      const excelData = await exportVTableToExcel(tableInstanceRef.current, { ignoreIcon: true });
      await downloadExcel(excelData, 'export');
    } catch (err) {
      console.error('Excel导出失败:', err);
    }
  }, []);

  const _exportToCsv = useCallback(async () => {
    if (!tableInstanceRef.current) return;
    try {
      const csvData = exportVTableToCsv(tableInstanceRef.current);
      await downloadCsv(csvData, 'export');
    } catch (err) {
      console.error('CSV导出失败:', err);
    }
  }, []);

  // ─── ★ Effect 1：只在 rebuildVersion 或 tableId 变化时重建表格 ─────────────────────

  useEffect(() => {
    if (!isMountedRef.current) return;
    _cleanupTable();

    // ★ 延迟一帧，确保 DOM 已就绪
    const timer = requestAnimationFrame(() => {
      if (isMountedRef.current) {
        _initializeTable();
      }
    });

    return () => cancelAnimationFrame(timer);
  }, [rebuildVersion, tableId, _cleanupTable, _initializeTable]);

  // ─── ★ Effect 2：数据变化时只更新 records，不重建表格 ─────────────────────

  const prevDataRef = useRef<any[]>([]);

  useEffect(() => {
    if (!isMountedRef.current) return;
    if (!tableInstanceRef.current) return;

    // ★ 用独立的 ref 跟踪上一次的数据引用（不受 render 阶段赋值干扰）
    if (currentData === prevDataRef.current) return;
    prevDataRef.current = currentData;

    try {
      tableInstanceRef.current.setRecords(currentData);
    } catch (err) {
      console.error('更新表格数据失败:', err);
    }
  }, [currentData]);

  // ─── Effect 3：ResizeObserver ─────────────────────

  useEffect(() => {
    if (!isMountedRef.current || !containerRef.current) return;
    resizeObserverRef.current = new ResizeObserver(_handleResize);
    resizeObserverRef.current.observe(containerRef.current);
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [_handleResize]);

  // ─── ★ Effect 4：通知父组件（严格去重，防循环） ─────────────────────

  const notifiedDataRef = useRef<any[] | null>(null);

  useEffect(() => {
    if (!isMountedRef.current) return;
    if (internalData.length === 0) return;

    // ★ 严格引用比较：同一个引用不重复通知
    if (internalData === notifiedDataRef.current) return;
    notifiedDataRef.current = internalData;

    // ★ 通过 ref 调用，避免 onDataUpdate 变化触发 effect 重新执行
    onDataUpdateRef.current(internalData);
  }, [internalData]); // ★ 只依赖 internalData，不依赖 onDataUpdate

  // ─── Effect 5：高亮变更单元格 ─────────────────────

  useEffect(() => {
    if (!isMountedRef.current || !changedCells || !tableInstanceRef.current) return;
    if (changedCells.length === 0) return;

    const cols = columnsRef.current;
    const data = currentDataRef.current;
    if (!cols.length || !data.length) return;

    const changedArea: any[] = [];
    changedCells.forEach((row: any) => {
      row.field.forEach((item: string) => {
        const colIndex = cols.findIndex((col: any) => col.field === item);
        const rowIndex = data.findIndex((rec: any) => rec.id === row.id);
        if (colIndex !== -1 && rowIndex !== -1) {
          changedArea.push({
            start: { col: colIndex + 1, row: rowIndex + 1 },
            end: { col: colIndex + 1, row: rowIndex + 1 },
          });
        }
      });
    });

    if (changedArea.length > 0) {
      tableInstanceRef.current.selectCells(changedArea);
    }
  }, [changedCells]);

  // ─── Effect 6：导出触发器 ─────────────────────

  useEffect(() => {
    if (exportExcelTrigger && exportExcelTrigger > 0) _exportToExcel();
  }, [exportExcelTrigger, _exportToExcel]);

  useEffect(() => {
    if (exportCsvTrigger && exportCsvTrigger > 0) _exportToCsv();
  }, [exportCsvTrigger, _exportToCsv]);

  // ─── Effect 7：组件卸载清理 ─────────────────────

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      _cleanupTable();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [_cleanupTable]);

  // ─── Render ─────────────────────

  if (error) {
    return (
      <div className="data-table-error-container">
        <div className="data-table-error-message">
          <h3>加载表格数据时出错</h3>
          <p>{error}</p>
          <button
            className="data-table-retry-button"
            onClick={() => {
              if (isMountedRef.current) {
                try { refreshData(tableId || undefined); } catch (err) { console.error('重试失败:', err); }
              }
            }}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <div className="data-table-wrapper">
        {loading && (
          <div className="data-table-loading-overlay">
            <div className="data-table-loading-spinner">
              <div className="data-table-spinner"></div>
              <span className="data-table-loading-text">加载中...</span>
            </div>
          </div>
        )}

        {/* ★ 无数据时显示空状态占位，而非空白 */}
        {!loading && !tableId && currentData.length === 0 && (
          <div
            style={{
              height: 'calc(100vh - 300px)',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: 14,
              userSelect: 'none',
            }}
          >
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16, opacity: 0.5 }}>
              <rect x="8" y="12" width="48" height="40" rx="4" stroke="#d1d5db" strokeWidth="2" fill="none" />
              <line x1="8" y1="24" x2="56" y2="24" stroke="#d1d5db" strokeWidth="2" />
              <line x1="24" y1="12" x2="24" y2="52" stroke="#d1d5db" strokeWidth="2" />
              <line x1="40" y1="12" x2="40" y2="52" stroke="#d1d5db" strokeWidth="2" />
              <line x1="8" y1="36" x2="56" y2="36" stroke="#e5e7eb" strokeWidth="1" />
              <line x1="8" y1="44" x2="56" y2="44" stroke="#e5e7eb" strokeWidth="1" />
            </svg>
            <span>暂无数据</span>
            <span style={{ fontSize: 12, color: '#d1d5db', marginTop: 4 }}>请先通过左侧对话生成表格数据</span>
          </div>
        )}

        <div
          ref={containerRef}
          className="vtable-container"
          style={{
            height: 'calc(100vh - 300px)',
            width: '100%',
            // ★ 无数据且未加载时隐藏空白容器
            display: (!loading && !tableId && currentData.length === 0) ? 'none' : undefined,
          }}
        />

        <AIInputModal
          isOpen={isAIModalOpen}
          onClose={_handleCloseAIModal}
          onSend={_handleAIQuestion}
          cellContext={currentCellContext}
        />
      </div>
    </div>
  );
};

export default DataTable;